import { Injectable } from "@angular/core";
import { Http, RequestOptions, Headers } from "@angular/http";
import { Router } from "@angular/router";
import { Subject, Observable, BehaviorSubject } from "rxjs";
import { map } from "rxjs/operators";

import { Config } from "src/app/config";
import { ServerResponse, SERVERRESPONSERESULT } from "src/app/models/serverresponse";
import { ServerResponseExamSave } from "src/app/models/serverresponse_examsave";
import { Exam } from "src/app/models/exam";
import { User } from "src/app/models/user";
import { UserAuthService } from "./userauth.service";

@Injectable()
export class ExamService {
  private exam: Exam;
  private user: User;
  private currentExamSubject: Subject<Exam>;
  private currentPageSubject: BehaviorSubject<number>;

  constructor(private http: Http, private router: Router, private userAuthService: UserAuthService) {
    this.currentExamSubject = new Subject<Exam>();
    this.currentPageSubject = new BehaviorSubject<number>(-1);
    this.createNew();
  }

  createNew(): void {
    this.exam = new Exam();
    this.currentExamSubject.next(this.exam);
  }

  loadEditExam(examId: number): Observable<boolean> {
    if (examId == -1) {
      this.createNew();
      const subject = new Subject<boolean>();
      setTimeout(() => subject.next(true), 0);
      return subject.asObservable();
    }
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "loadeditexam",
      examid: examId,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          if (json.result == SERVERRESPONSERESULT.OK) {
            this.exam = new Exam(json.exam);
            this.currentExamSubject.next(this.exam);
            return true;
          }
          return false;
        } catch (err) {
          console.error(err);
          return false;
        }
      }),
    );
  }

  saveEditExam(): Observable<ServerResponseExamSave> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "saveeditexam",
      exam: this.exam,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          // A refusal (no session, say) carries no id, so only an OK answer moves to a new one
          if (json.result == SERVERRESPONSERESULT.OK && json.id != this.exam.id) {
            this.router.navigate(["edit", json.id]);
          }
          return json;
        } catch (err) {
          console.error(err);
          let ret: ServerResponseExamSave = {
            result: SERVERRESPONSERESULT.Error,
            error: "Server error",
          };
          return ret;
        }
      }),
    );
  }

  loadUserExam(examId: number, userId: number): Observable<boolean> {
    if (examId == -1) {
      this.createNew();
      const subject = new Subject<boolean>();
      setTimeout(() => subject.next(true), 0);
      return subject.asObservable();
    }
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    headers.append("Authorization", "Bearer " + this.userAuthService.userToken);
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "loaduserexam",
      examid: examId,
      userid: userId,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          if (json.result == SERVERRESPONSERESULT.OK) {
            this.exam = new Exam(json.exam);
            this.currentExamSubject.next(this.exam);
            return true;
          }
          return false;
        } catch (err) {
          console.error(err);
          return false;
        }
      }),
    );
  }

  saveUserExam(userId: number): Observable<ServerResponse> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    headers.append("Authorization", "Bearer " + this.userAuthService.userToken);
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "saveuserexam",
      userid: userId,
      exam: this.exam,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          return json;
        } catch (err) {
          console.error(err);
          let ret: ServerResponse = {
            result: SERVERRESPONSERESULT.Error,
            error: "Server error",
          };
          return ret;
        }
      }),
    );
  }

  submitUserExam(userId: number): Observable<ServerResponse> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    headers.append("Authorization", "Bearer " + this.userAuthService.userToken);
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "submituserexam",
      userid: userId,
      exam: this.exam,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          return json;
        } catch (err) {
          console.error(err);
          let ret: ServerResponse = {
            result: SERVERRESPONSERESULT.Error,
            error: "Server error",
          };
          return ret;
        }
      }),
    );
  }

  loadExamResults(userExamId: number): Observable<boolean> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    if (this.userAuthService.userToken) {
      headers.append("Authorization", "Bearer " + this.userAuthService.userToken);
    }
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "loadexamresults",
      userexamid: userExamId,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          if (json.result == SERVERRESPONSERESULT.OK) {
            this.exam = new Exam(json.exam);
            this.user = json.user;
            this.currentExamSubject.next(this.exam);
            return true;
          }
          return false;
        } catch (err) {
          console.error(err);
          return false;
        }
      }),
    );
  }

  saveExamResults(userExamId: number): Observable<ServerResponse> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "saveexamresults",
      userexamid: userExamId,
      exam: this.exam,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          return json;
        } catch (err) {
          console.error(err);
          let ret: ServerResponse = {
            result: SERVERRESPONSERESULT.Error,
            error: "Server error",
          };
          return ret;
        }
      }),
    );
  }

  submitExamResults(userExamId: number): Observable<ServerResponse> {
    const headers: Headers = new Headers();
    headers.append("X-Requested-With", "XMLHttpRequest");
    const opts: RequestOptions = new RequestOptions();
    opts.headers = headers;
    const data = {
      opt: "submitexamresult",
      userexamid: userExamId,
      exam: this.exam,
    };

    return this.http.post(Config.SERVER_API, data, opts).pipe(
      map(response => {
        try {
          let json = response.json();
          return json;
        } catch (err) {
          console.error(err);
          let ret: ServerResponse = {
            result: SERVERRESPONSERESULT.Error,
            error: "Server error",
          };
          return ret;
        }
      }),
    );
  }

  get onCurrentExamChange(): Observable<Exam> {
    return this.currentExamSubject.asObservable();
  }

  get onCurrentPageChange(): Observable<number> {
    return this.currentPageSubject.asObservable();
  }

  setCurrentPage(page: number): void {
    this.currentPageSubject.next(page);
  }

  get currentExam(): Exam {
    return this.exam;
  }

  get currentUser(): User {
    return this.user;
  }
}
